import { Assets, Container, Graphics, Sprite, Texture } from "pixi.js";
import { MAP_BACKGROUND_ASSET, MAP_LANDMARKS } from "../map-content";
import type { MapLandmark } from "../map-content";
import type { RegionSnapshot } from "@frontier/protocol/contracts";
import type { Point } from "./geometry";
import {
  backdropLandmarkPresentation,
  backdropPicturePresentation,
  wrappedBackdropCoordinate,
} from "./backdrop-presentation";

interface Star {
  sprite: Sprite;
  x: number;
  y: number;
  depth: number;
  phase: number;
}

interface LandmarkView {
  definition: MapLandmark;
  sprite: Sprite;
  nativeScale: number;
}

export class Backdrop {
  readonly container = new Container();
  private readonly stars: Star[] = [];
  private readonly dust: Star[] = [];
  private readonly clouds: Sprite[] = [];
  private readonly cloudTexture: Texture;
  private readonly landmarks: LandmarkView[] = [];
  private readonly weather = new Graphics();
  private regions: readonly RegionSnapshot[] = [];
  private picture: Sprite | undefined;
  private disposed = false;

  constructor() {
    this.container.sortableChildren = true;
    this.weather.zIndex = 2000;
    this.container.addChild(this.weather);
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Не удалось создать фон сектора");
    const gradient = context.createRadialGradient(128, 128, 4, 128, 128, 128);
    gradient.addColorStop(0, "rgba(255,255,255,0.12)");
    gradient.addColorStop(0.3, "rgba(255,255,255,0.07)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);
    this.cloudTexture = Texture.from(canvas);
    for (const color of [0x146d7b, 0x173457, 0x533775]) {
      const cloud = new Sprite({
        texture: this.cloudTexture,
        anchor: 0.5,
        tint: color,
      });
      this.clouds.push(cloud);
      this.container.addChild(cloud);
    }
    let seed = 41271;
    const random = (): number => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    for (let index = 0; index < 170; index += 1) {
      const depth = random() * 0.12 + 0.025;
      const sprite = new Sprite({
        texture: Texture.WHITE,
        tint: index % 7 === 0 ? 0x9ee8f4 : 0xb6c6dd,
      });
      const size = random() > 0.94 ? 2 : 1;
      sprite.width = size;
      sprite.height = size;
      sprite.alpha = random() * 0.6 + 0.15;
      this.stars.push({
        sprite,
        x: random(),
        y: random(),
        depth,
        phase: random() * 6.28,
      });
      this.container.addChild(sprite);
    }
    for (let index = 0; index < 28; index += 1) {
      const sprite = new Sprite({
        texture: Texture.WHITE,
        tint: index % 4 === 0 ? 0xa5c5d4 : 0x6e899e,
        anchor: 0.5,
        alpha: 0.18,
      });
      sprite.width = index % 5 === 0 ? 2 : 1;
      sprite.height = sprite.width;
      sprite.zIndex = 1800;
      this.dust.push({
        sprite,
        x: random(),
        y: random(),
        depth: 0.65 + random() * 0.25,
        phase: 0,
      });
      this.container.addChild(sprite);
    }
    void this.loadArtwork();
  }

  setRegions(regions: readonly RegionSnapshot[]): void {
    this.regions = regions;
  }

  render(
    width: number,
    height: number,
    cameraX: number,
    cameraY: number,
    now: number,
    zoom = 1,
    offset: Point = { x: 0, y: 0 },
    reducedMotion = false,
  ): void {
    const region = this.regions.find(
      (item) =>
        cameraX >= item.x &&
        cameraX < item.x + item.width &&
        cameraY >= item.y &&
        cameraY < item.y + item.height,
    );
    const localCameraX = region ? cameraX - region.x - region.width / 2 : 0;
    const localCameraY = region ? cameraY - region.y - region.height / 2 : 0;
    const camera = { x: cameraX, y: cameraY };
    const viewport = { width, height };
    const motionOffset = reducedMotion ? { x: 0, y: 0 } : offset;
    if (this.picture) {
      this.picture.visible = true;
      const picture = backdropPicturePresentation(
        viewport,
        this.picture.texture,
        camera,
        region,
        zoom,
        motionOffset,
      );
      this.picture.scale.set(picture.scale);
      this.picture.position.set(picture.x, picture.y);
      this.picture.tint =
        region?.weather === "ion-storm"
          ? 0xb4b1e6
          : region?.weather === "debris-field"
            ? 0xd5ba97
            : 0xffffff;
    }
    this.clouds.forEach((cloud, index) => {
      cloud.visible = this.picture === undefined;
      cloud.position.set(
        width * (index * 0.35 + 0.1) -
          localCameraX * 0.025 +
          motionOffset.x * 0.35,
        height * (0.4 + Math.sin(index) * 0.3) -
          localCameraY * 0.02 +
          motionOffset.y * 0.35,
      );
      cloud.width = width * 1.8;
      cloud.height = height * 2.4;
    });
    for (const star of this.stars) {
      star.sprite.x = wrappedBackdropCoordinate(
        star.x * width - cameraX * star.depth * zoom + motionOffset.x * 0.75,
        width,
      );
      star.sprite.y = wrappedBackdropCoordinate(
        star.y * height - cameraY * star.depth * zoom + motionOffset.y * 0.75,
        height,
      );
      star.sprite.alpha =
        (0.28 +
          (reducedMotion ? 0 : Math.sin(now / 4000 + star.phase) * 0.18) +
          star.depth * 2) *
        (this.picture ? 0.55 : 1);
    }
    for (const particle of this.dust) {
      particle.sprite.x = wrappedBackdropCoordinate(
        particle.x * width -
          cameraX * particle.depth * zoom +
          motionOffset.x * 0.2,
        width,
      );
      particle.sprite.y = wrappedBackdropCoordinate(
        particle.y * height -
          cameraY * particle.depth * zoom +
          motionOffset.y * 0.2,
        height,
      );
    }
    for (const { sprite, definition, nativeScale } of this.landmarks) {
      const presentation = backdropLandmarkPresentation(
        definition,
        camera,
        viewport,
        zoom,
        region,
        motionOffset,
      );
      sprite.visible = presentation.visible;
      sprite.position.set(presentation.x, presentation.y);
      sprite.scale.set(nativeScale * presentation.scale);
    }
    this.weather.clear();
    if (region?.weather === "ion-storm") {
      for (let index = 0; index < 18; index += 1) {
        const x =
          (((index * 131 + (reducedMotion ? 0 : now * 0.018) - cameraX * 0.05) %
            width) +
            width) %
          width;
        const y =
          (((index * 97 + (reducedMotion ? 0 : now * 0.045)) % height) +
            height) %
          height;
        this.weather
          .moveTo(x, y)
          .lineTo(x - 6, y + 18)
          .stroke({ color: 0x93acff, width: 1, alpha: 0.16 });
      }
    } else if (region?.weather === "debris-field") {
      for (let index = 0; index < 24; index += 1) {
        const x = (((index * 103 - cameraX * 0.14) % width) + width) % width;
        const y = (((index * 73 - cameraY * 0.14) % height) + height) % height;
        this.weather
          .circle(x, y, index % 3 === 0 ? 2 : 1)
          .fill({ color: 0xc8a586, alpha: 0.2 });
      }
    }
  }

  destroy(): void {
    this.disposed = true;
    this.container.destroy({ children: true });
    this.cloudTexture.destroy(true);
  }

  private async loadArtwork(): Promise<void> {
    const background = Assets.load<Texture>(MAP_BACKGROUND_ASSET)
      .then((texture) => {
        if (this.disposed) return;
        this.picture = new Sprite({ texture, anchor: 0.5, alpha: 0.8 });
        this.picture.visible = false;
        this.picture.zIndex = -100;
        this.picture.label = "deep-space";
        this.container.addChildAt(this.picture, 0);
      })
      .catch(() =>
        console.warn(
          "Sector background unavailable; keeping the procedural star field",
        ),
      );
    const landmarks = MAP_LANDMARKS.map(async (definition) => {
      try {
        const texture = await Assets.load<Texture>(definition.asset);
        if (this.disposed) return;
        const sprite = new Sprite({
          texture,
          anchor: 0.5,
          tint: definition.tint ?? 0xffffff,
        });
        sprite.visible = false;
        sprite.zIndex = Math.round(definition.parallax * 1000);
        sprite.label = `landmark:${definition.id}`;
        this.landmarks.push({
          definition,
          sprite,
          nativeScale:
            definition.size / Math.max(texture.width, texture.height),
        });
        this.container.addChild(sprite);
        this.container.sortChildren();
      } catch {
        console.warn(`Decorative landmark unavailable: ${definition.id}`);
      }
    });
    await Promise.all([background, ...landmarks]);
  }
}
